import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import { ArrowLeft, Save, Loader2, Phone, Unplug, Sparkles, Send, Plus, Trash2 } from 'lucide-react';
import { getApiError } from '../../lib/api';
import {
  normalizeAgentBusinessInformation,
  validateBusinessInformationForSave,
} from '../../lib/businessInformation';

export default function AgentConfigurationScreen({
  roleLabel,
  backPath,
  getConfiguration,
  saveConfiguration,
  generateConfiguration,
  attachTwilioNumber,
  detachTwilioNumber,
  patchFacebookEnabled,
}) {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [generatingConfig, setGeneratingConfig] = useState(false);
  const [savingTwilio, setSavingTwilio] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [status, setStatus] = useState('draft');
  const [vapiAgentId, setVapiAgentId] = useState('');
  const [configurationText, setConfigurationText] = useState('{\n  \n}');
  const [twilioNumber, setTwilioNumber] = useState('');
  const [attachedTwilioNumber, setAttachedTwilioNumber] = useState('');
  const [facebookEnabled, setFacebookEnabled] = useState(false);
  const [facebookSaving, setFacebookSaving] = useState(false);
  const [businessInformation, setBusinessInformation] = useState(() =>
    normalizeAgentBusinessInformation(null)
  );
  const [savingBusiness, setSavingBusiness] = useState(false);

  const hasTwilioAttached = Boolean(attachedTwilioNumber);

  const businessInformationValidation = useMemo(
    () => validateBusinessInformationForSave(businessInformation),
    [businessInformation]
  );

  const normalizedBackPath = useMemo(() => {
    if (typeof backPath === 'function') return backPath(agentId);
    return backPath;
  }, [backPath, agentId]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await getConfiguration(agentId);
      const payload = response?.data || {};
      const agent = payload.agent || payload || {};
      const configuration =
        payload.configuration ??
        agent.configuration ??
        agent.config_json ??
        agent.agent_config ??
        {};

      setAgentName(agent.name || 'Agent');
      setBusinessId(
        payload.business_id ||
        payload.customer_id ||
        agent.business_id ||
        agent.customer_id ||
        ''
      );
      setStatus(agent.status || 'draft');
      setVapiAgentId(agent.vapi_agent_id || '');
      setAttachedTwilioNumber(
        payload.twilio_phone_number || agent.twilio_phone_number || agent.phone_number || ''
      );
      setConfigurationText(JSON.stringify(configuration, null, 2));
      setFacebookEnabled(agent.facebook_enabled === true);
      setBusinessInformation(
        normalizeAgentBusinessInformation(agent.businessInformation ?? null)
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load agent configuration');
      navigate(normalizedBackPath);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, [agentId]);

  const handleFacebookToggle = async (checked) => {
    if (!patchFacebookEnabled) return;
    try {
      setFacebookSaving(true);
      await patchFacebookEnabled(agentId, { facebook_enabled: checked });
      setFacebookEnabled(checked);
      toast.success(checked ? 'Facebook messaging enabled' : 'Facebook messaging disabled');
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Could not update Facebook setting');
    } finally {
      setFacebookSaving(false);
    }
  };

  const handleGenerateConfiguration = async () => {
    if (!generateConfiguration) return;
    try {
      setGeneratingConfig(true);
      const response = await generateConfiguration(agentId, {
        mode: 'replace',
        include_empty: false,
      });
      const payload = response?.data || {};
      const configuration = payload.configuration ?? payload;
      if (configuration && typeof configuration === 'object' && !Array.isArray(configuration)) {
        setConfigurationText(JSON.stringify(configuration, null, 2));
        toast.success('Configuration generated. Review and save when ready.');
      } else {
        toast.error('Server returned an invalid configuration object');
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to generate configuration');
    } finally {
      setGeneratingConfig(false);
    }
  };

  const mergeBusinessInformationFromResponse = (response) => {
    const agent = response?.data?.agent;
    if (agent && Object.prototype.hasOwnProperty.call(agent, 'businessInformation')) {
      setBusinessInformation(normalizeAgentBusinessInformation(agent.businessInformation));
    }
  };

  const handleSaveConfiguration = async () => {
    let parsed;
    try {
      parsed = JSON.parse(configurationText);
    } catch {
      toast.error('Configuration JSON is invalid');
      return;
    }

    try {
      setSavingConfig(true);
      const response = await saveConfiguration(agentId, { configuration: parsed });
      mergeBusinessInformationFromResponse(response);
      toast.success('Agent configuration saved');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSaveBusinessInformation = async () => {
    const v = validateBusinessInformationForSave(businessInformation);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }

    try {
      setSavingBusiness(true);
      const response = await saveConfiguration(agentId, { businessInformation });
      mergeBusinessInformationFromResponse(response);
      toast.success('Business information saved');
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Failed to save business information');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleClearBusinessInformation = async () => {
    try {
      setSavingBusiness(true);
      await saveConfiguration(agentId, { businessInformation: null });
      setBusinessInformation(normalizeAgentBusinessInformation(null));
      toast.success('Business information cleared');
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Failed to clear business information');
    } finally {
      setSavingBusiness(false);
    }
  };

  const updateSection = (index, field, value) => {
    setBusinessInformation((prev) => {
      const sections = [...prev.sections];
      const row = { ...sections[index], [field]: value };
      sections[index] = row;
      return { ...prev, sections };
    });
  };

  const addSection = () => {
    setBusinessInformation((prev) => ({
      ...prev,
      sections: [...prev.sections, { heading: '', body: '' }],
    }));
  };

  const removeSection = (index) => {
    setBusinessInformation((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index),
    }));
  };

  const updateReferenceLink = (index, field, value) => {
    setBusinessInformation((prev) => {
      const referenceLinks = [...prev.referenceLinks];
      const row = { ...referenceLinks[index], [field]: value };
      referenceLinks[index] = row;
      return { ...prev, referenceLinks };
    });
  };

  const addReferenceLink = () => {
    setBusinessInformation((prev) => ({
      ...prev,
      referenceLinks: [...prev.referenceLinks, { label: '', url: '', notes: '' }],
    }));
  };

  const removeReferenceLink = (index) => {
    setBusinessInformation((prev) => ({
      ...prev,
      referenceLinks: prev.referenceLinks.filter((_, i) => i !== index),
    }));
  };

  const handleAttachTwilio = async () => {
    if (!twilioNumber.trim()) {
      toast.error('Enter a Twilio phone number');
      return;
    }

    try {
      setSavingTwilio(true);
      await attachTwilioNumber(agentId, { phone_number: twilioNumber.trim() });
      setAttachedTwilioNumber(twilioNumber.trim());
      setTwilioNumber('');
      toast.success('Twilio phone number attached');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to attach Twilio number');
    } finally {
      setSavingTwilio(false);
    }
  };

  const handleDetachTwilio = async () => {
    try {
      setSavingTwilio(true);
      await detachTwilioNumber(agentId);
      setAttachedTwilioNumber('');
      toast.success('Twilio phone number detached');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to detach Twilio number');
    } finally {
      setSavingTwilio(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-56 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid={`${roleLabel.toLowerCase()}-agent-config-page`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(normalizedBackPath)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="space-y-1">
        <h1 className="text-2xl font-jakarta font-bold text-slate-900">Configure Agent</h1>
        <p className="text-slate-500">
          Update JSON configuration, business copy and reference links, and attach a Twilio phone number ({roleLabel})
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {agentName}
            <Badge variant={status === 'active' ? 'default' : 'secondary'}>{status}</Badge>
          </CardTitle>
          <CardDescription>Agent ID: {agentId}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-600">
          {vapiAgentId && <p>VAPI Agent ID: {vapiAgentId}</p>}
        </CardContent>
      </Card>

      {patchFacebookEnabled ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Facebook messaging
            </CardTitle>
            <CardDescription>
              Turn on only when this agent should respond on Facebook Messenger. Updates an API flag independent of the
              JSON configuration block below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3">
              <span className="text-sm font-medium text-slate-800">Enable Facebook messaging for this agent</span>
              <Switch
                checked={facebookEnabled}
                onCheckedChange={handleFacebookToggle}
                disabled={facebookSaving}
                aria-label="Facebook messaging"
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Business information</CardTitle>
          <CardDescription>
            Instructions, sections, and reference links for the agent model. Saving this block does not change the JSON
            configuration above; the API stores it separately. Reference link URLs must start with http:// or https://.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="business-instruction">Instruction for model</Label>
            <Textarea
              id="business-instruction"
              className="min-h-[100px]"
              value={businessInformation.instructionForModel}
              onChange={(e) =>
                setBusinessInformation((prev) => ({ ...prev, instructionForModel: e.target.value }))
              }
              placeholder="High-level guidance for how the agent should represent the business..."
            />
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-base">Sections</Label>
              <Button type="button" variant="outline" size="sm" onClick={addSection}>
                <Plus className="w-4 h-4 mr-1" />
                Add section
              </Button>
            </div>
            {businessInformation.sections.length === 0 ? (
              <p className="text-sm text-slate-500">No sections yet. Sections are optional.</p>
            ) : (
              <div className="space-y-4">
                {businessInformation.sections.map((section, index) => (
                  <div
                    key={`section-${index}`}
                    className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3"
                  >
                    <div className="flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeSection(index)}>
                        <Trash2 className="w-4 h-4 mr-1 text-slate-600" />
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`section-heading-${index}`}>Heading</Label>
                      <Input
                        id={`section-heading-${index}`}
                        value={section.heading}
                        onChange={(e) => updateSection(index, 'heading', e.target.value)}
                        placeholder="Section title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`section-body-${index}`}>Body</Label>
                      <Textarea
                        id={`section-body-${index}`}
                        value={section.body}
                        onChange={(e) => updateSection(index, 'body', e.target.value)}
                        placeholder="Section content"
                        className="min-h-[80px]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-base">Reference links</Label>
              <Button type="button" variant="outline" size="sm" onClick={addReferenceLink}>
                <Plus className="w-4 h-4 mr-1" />
                Add link
              </Button>
            </div>
            {businessInformation.referenceLinks.length === 0 ? (
              <p className="text-sm text-slate-500">No reference links. Links are optional unless you add rows.</p>
            ) : (
              <div className="space-y-4">
                {businessInformation.referenceLinks.map((link, index) => (
                  <div
                    key={`ref-${index}`}
                    className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 space-y-3"
                  >
                    <div className="flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeReferenceLink(index)}>
                        <Trash2 className="w-4 h-4 mr-1 text-slate-600" />
                        Remove
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ref-label-${index}`}>Label</Label>
                      <Input
                        id={`ref-label-${index}`}
                        value={link.label}
                        onChange={(e) => updateReferenceLink(index, 'label', e.target.value)}
                        placeholder="Link label"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ref-url-${index}`}>URL</Label>
                      <Input
                        id={`ref-url-${index}`}
                        value={link.url}
                        onChange={(e) => updateReferenceLink(index, 'url', e.target.value)}
                        placeholder="https://example.com"
                        className="font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`ref-notes-${index}`}>Notes</Label>
                      <Textarea
                        id={`ref-notes-${index}`}
                        value={link.notes}
                        onChange={(e) => updateReferenceLink(index, 'notes', e.target.value)}
                        placeholder="Optional context for the model"
                        className="min-h-[60px]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!businessInformationValidation.ok && (
              <p className="text-sm text-amber-700" role="status">
                {businessInformationValidation.message}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleSaveBusinessInformation}
              disabled={savingBusiness || !businessInformationValidation.ok}
            >
              {savingBusiness ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save business information
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClearBusinessInformation}
              disabled={savingBusiness}
            >
              Clear stored business information
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1.5">
              <CardTitle>Agent Configuration (JSON)</CardTitle>
              <CardDescription>
                Paste valid JSON used by backend/VAPI provisioning. You can update this later.
              </CardDescription>
            </div>
            {generateConfiguration && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleGenerateConfiguration}
                disabled={generatingConfig || savingConfig}
                title="Generate JSON from the agent data already stored for this agent"
              >
                {generatingConfig ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Auto-generate
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="agent-config-json">Configuration JSON</Label>
            <Textarea
              id="agent-config-json"
              className="font-mono min-h-[280px]"
              value={configurationText}
              onChange={(e) => setConfigurationText(e.target.value)}
              placeholder='{"greeting":"Hello","business":{"name":"Demo"}}'
            />
          </div>
          <Button onClick={handleSaveConfiguration} disabled={savingConfig}>
            {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Twilio Phone Number</CardTitle>
          <CardDescription>
            Optional at agent creation time. You can attach or detach a Twilio number any time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {['Service', 'Admin'].includes(roleLabel) && businessId && (
            <Button
              variant="outline"
              onClick={() => {
                const basePath = roleLabel === 'Admin' ? '/admin/customers' : '/service/businesses';
                navigate(`${basePath}/${businessId}/twilio?agentId=${encodeURIComponent(agentId)}`);
              }}
            >
              Manage Twilio Account Config
            </Button>
          )}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-500 mb-1">Currently attached</p>
            <p className="text-sm font-medium text-slate-900">{attachedTwilioNumber || 'None'}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="twilio-number">Twilio Number</Label>
            <Input
              id="twilio-number"
              value={twilioNumber}
              onChange={(e) => setTwilioNumber(e.target.value)}
              placeholder="+61400000000"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleAttachTwilio} disabled={savingTwilio}>
              {savingTwilio ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              Attach Number
            </Button>
            <Button variant="outline" onClick={handleDetachTwilio} disabled={savingTwilio || !hasTwilioAttached}>
              <Unplug className="w-4 h-4 mr-2" />
              Detach Number
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
